import React from 'react';
import { Link } from 'react-router-dom';
import ServiceGrid from './ServiceGrid';
import FAQ from './FAQ';
import SEO from './SEO';
import { BLOG_POSTS, BlogTile } from './Blog.jsx';

const SERVICES = [
  'Pressure Washing',
  'Window Cleaning',
  'Gutter Cleaning',
  'Driveway Cleaning',
  'Sidewalk Cleaning',
  'Patio Cleaning',
  'Deck Cleaning',
  'Fence Cleaning',
  'Solar Panel Cleaning',
  'Pool Deck Cleaning',
  'Concrete Cleaning',
  'Graffiti Removal',
];

const HOME_FAQ = [
  {
    question: 'How can I request a quote for your pressure washing services?',
    answer: `Requesting a quote is easy! You can reach out to us by calling our dedicated customer service team at (602) 730-0728, or send us an email at coppercanyonpressurewash@gmail.com. One of our friendly representatives will promptly get in touch with you to discuss your specific cleaning needs and provide you with a customized quote tailored to your requirements.`
  },
  {
    question: 'Is pressure washing safe for all surfaces?',
    answer: {
      intro: 'Generally yes, we specialize in cleaning a wide variety of surfaces, including:',
      list: [
        'Concrete driveways and sidewalks',
        'Asphalt driveways',
        'Brick and stone patios, walls, and walkways',
        'Pool decks and tile',
        'Wood decks and fences',
        'Commercial storefronts, parking lots, and buildings'
      ],
      outro: `If you're unsure about a particular surface, just let us know, and we can advise on the best cleaning method.`
    }
  },
  {
    question: 'Will pressure washing damage my plants or landscaping?',
    answer: `We take great care in protecting your landscaping. Our team will cover or avoid delicate plants and outdoor features to prevent damage. We also use environmentally friendly, non-toxic cleaning solutions to ensure the safety of your garden and surrounding area!`
  },
  {
    question: 'Do I need to be home for the service?',
    answer: `While it's not required for you to be home during the service, we do recommend it so we can discuss any specific needs or concerns you may have before we begin. If you're unavailable, we can still proceed as long as we have access to the property and any necessary instructions.`
  },
  {
    question: 'Do you offer discounts or promotions?',
    answer: `Yes! We often run promotions or offer discounts for new customers, repeat customers, or bundled services. Be sure to ask about any available deals when you schedule your service.`
  },
  {
    question: "Can pressure washing increase my home's value?",
    answer: `Yes! A clean, well-maintained exterior can greatly enhance your home's curb appeal, which is a key factor in increasing property value. Whether you're selling your home or just maintaining its appearance, pressure washing can make a significant difference.`
  },
  {
    question: 'How often should I have my property pressure washed?',
    answer: {
      intro: 'The frequency of pressure washing depends on various factors like weather, location, and the condition of your surfaces. For example:',
      list: [
        'Driveways and sidewalks typically need cleaning once a year or whenever they become visibly dirty.',
        'Dumpster Pads should be cleaned every month to prevent mold, mildew, and dirt buildup.',
        'Decks and patios should be cleaned annually to maintain their appearance and prevent weathering.'
      ],
      outro: `We recommend a free consultation to assess your property's specific needs and give you a tailored cleaning schedule.`
    }
  }
];

function Home() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'Copper Canyon Pressure Wash',
    'image': '/Copper Canyon Pressure Wash_final logo.png',
    'description': 'Professional pressure washing services in Phoenix, Scottsdale, Mesa, Tempe, Gilbert, and Paradise Valley. Residential and commercial cleaning solutions.',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Phoenix',
      'addressRegion': 'AZ',
      'addressCountry': 'US'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '33.4484',
      'longitude': '-112.0740'
    },
    'url': 'https://coppercanyonpressurewash.com',
    'telephone': '+14807890990',
    'priceRange': '$$',
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
      ],
      'opens': '08:00',
      'closes': '18:00'
    },
    'sameAs': [
      'https://www.facebook.com/people/copper-canyon-pressure-wash/61570012767538',
      'https://www.instagram.com/copper.canyon.p.w/'
    ]
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Copper Canyon Pressure Wash',
    'url': 'https://coppercanyonpressurewash.com',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': 'https://coppercanyonpressurewash.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    },
    'image': {
      '@type': 'ImageObject',
      'url': [
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.avif',
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.webp',
        'https://coppercanyonpressurewash.com/Copper Canyon Pressure Wash_final logo.png'
      ]
    }
  };

  return (
    <main className="flex flex-col">
      <SEO 
        title="Copper Canyon Pressure Wash | Phoenix Pressure Washing"
        description="Transform your property with expert pressure washing in Phoenix, Scottsdale, Mesa & more. Residential & commercial. Free quotes. Satisfaction guaranteed!"
        schema={organizationSchema}
        keywords="Phoenix pressure washing, Scottsdale power washing, Mesa exterior cleaning, Tempe house washing, Gilbert driveway cleaning, Paradise Valley deck cleaning, commercial cleaning, residential cleaning, property maintenance, curb appeal, home value"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Hero Section */}
        <section className="relative h-[600px] w-full overflow-hidden" aria-label="Hero">
          <div className="absolute inset-0">
            <picture>
              <source srcSet="/Hero_Image.avif" type="image/avif" />
              <source srcSet="/Hero_Image.webp" type="image/webp" />
            <img 
              src="/Hero_Image.JPG" 
              alt="Professional pressure washing service in action, showing a clean driveway and home exterior" 
                title="Professional Pressure Washing Services - Transform Your Property"
              className="w-full h-full object-cover object-center"
                width="1600"
                height="600"
            />
            </picture>
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
                Professional Pressure Washing Services
              </h1>
              <p className="text-xl sm:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
                Transform your property with our expert cleaning solutions
              </p>
              <div className="flex justify-center gap-4">
                <a 
                  href="tel:6027300728"
                  className="inline-block px-8 py-3 bg-[#8B4513] text-white font-medium rounded-lg shadow-md hover:bg-[#6B3410] transition-colors duration-200 ease-in-out"
                  aria-label="Call us at (602) 730-0728"
                >
                  Call Now
                </a>
                <Link 
                  to="/contact"
                  className="inline-block px-8 py-3 bg-white text-[#4a3f35] font-medium rounded-lg shadow-md hover:bg-gray-100 transition-colors duration-200 ease-in-out"
                >
                  Get Free Quote
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-white" aria-labelledby="services-heading">
          <h2 id="services-heading" className="text-3xl font-bold text-[#4a3f35] mb-8 text-center">Our Services</h2>
          <ServiceGrid services={SERVICES} />
        </section>

        {/* FAQ Section */}
        <FAQ items={HOME_FAQ} title="Common Questions About Our Services" />
        {/* Blog Section */}
        <section className="py-16 bg-white" aria-labelledby="blog-section-heading">
          <h2 id="blog-section-heading" className="text-3xl font-bold text-[#4a3f35] mb-8 text-center">From Our Blog</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-10 text-center">
            Explore our latest tips, guides, and expert advice on pressure washing, exterior cleaning, and property care in the Phoenix area.
          </p>
          <div className="mx-auto w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.map(post => (
              <BlogTile key={post.slug} post={post} />
            ))}
          </div>
          <div className="flex justify-center mt-8">
            <Link 
              to="/blog" 
              className="inline-block px-6 py-2 bg-white border-2 border-[#8B4513] text-[#8B4513] font-medium rounded-lg shadow-sm hover:bg-[#f8f7f3] hover:border-[#6B3410] hover:text-[#6B3410] transition-colors duration-200"
            >
              See all articles
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Home; 